import { gql, useLazyQuery, useMutation } from '@apollo/client';
import CloseIcon from '@mui/icons-material/Close';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { DroppableFileInput } from '@ovComponents/2-component/droppableFileInput/droppableFileInput';
import { FileTypes } from '@ovComponents/2-component/droppableFileInput/fileTypes';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import { InlineImage } from '@ovComponents/2-component/inlineImage/inlineImage';
import { base64ToFile } from '@ovComponents/4-module/workflowCompletion/subSteps/utils';
import { DELETE_FILE_DOCUMENT } from '@ovComponents/5-page/manageNewsAndInsights/components/queries';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { UserContext } from '@providers/userContextProvider';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CREATE_FILE_DOCUMENT,
  FETCH_FILE_UPLOAD_URL,
} from '../addBankAccount/addBankAccountManually/addBankAccountManually';

const UPDATE_ORGANIZATION_USER_AVATAR = gql`
  mutation updateOrganizationUserProfile(
    $input: UpdateOrganizationUserProfileInput!
  ) {
    updateOrganizationUserProfile(input: $input) {
      organizationUser {
        id
        avatar
      }
    }
  }
`;

export const ChangeAvatarModal = ({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
}) => {
  const { t } = useTranslation(['components', 'shared']);
  const { sys } = useThemeTokens();
  const [newAvatar, setNewAvatar] = useState<string | null>(null);
  const { userContext, refetch } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [fetchFileUploadUrl] = useLazyQuery(FETCH_FILE_UPLOAD_URL, {
    fetchPolicy: 'no-cache',
  });
  const [createFileDocument] = useMutation(CREATE_FILE_DOCUMENT);
  const [deleteFileDocument] = useMutation(DELETE_FILE_DOCUMENT);

  const [updateOrganizationUserAvatar] = useMutation(
    UPDATE_ORGANIZATION_USER_AVATAR,
  );

  const doUpload = async (): Promise<any> => {
    setLoading(true);
    const uploadingFile = base64ToFile(newAvatar!, 'avatar');
    const createFileInput: any = {
      objectType: 'ORGANIZATION_USER',
      objectId: userContext.id,
      fileName: 'avatar',
      type: 'AVATAR',
    };

    /* (1) fetch the S3 upload URL from backend */
    const queryResult = await fetchFileUploadUrl({
      variables: {
        input: {
          ...createFileInput,
          userId: userContext.id,
          generateUniqueFileName: true,
        },
      },
    });
    const uploadUrl = queryResult?.data?.fetchFileUploadUrl.temporarySignedURL;
    const timestamp = queryResult?.data?.fetchFileUploadUrl.timestamp;
    if (!uploadUrl || queryResult?.error) {
      return;
    }
    /* (2) do the upload */
    try {
      const uploaded: Response = await fetch(
        new Request(uploadUrl, { method: 'PUT', body: uploadingFile }),
      );
      if (!uploaded.ok)
        throw new Error(`${uploaded.status} ${uploaded.statusText}`);
    } catch {
      return;
    }

    /* (3) create the fileDocument within backend */
    createFileInput.name = uploadingFile.name;
    createFileInput.mediaType = uploadingFile.type;
    createFileInput.permissionType = 'PUBLIC';
    createFileInput.sourceType = 'ORGANIZATION_USER';
    createFileInput.timestamp = timestamp;

    await createFileDocument({
      variables: { input: createFileInput },
      onCompleted(data: any) {
        updateOrganizationUserAvatar({
          variables: {
            input: {
              avatar: data.createFileDocument.fileDocument.id,
            },
          },
          onCompleted: async () => {
            setLoading(false);
            handleClose();
          },
        });
      },
    });
  };

  const setAvatar = async (file: File) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64String = reader.result as string;
      setNewAvatar(base64String);
    };
  };

  const deleteFile = async () => {
    await deleteFileDocument({
      variables: { fileDocumentId: userContext.avatar },
      onCompleted: async () => {
        setNewAvatar(null);
        await updateOrganizationUserAvatar({
          variables: {
            input: {
              avatar: null,
            },
          },
        });
        refetch();
      },
    });
  };

  const handleClose = () => {
    setOpen(false);
    setNewAvatar(null);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='xs' fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          borderBottom: `1px ${sys.color.primaryContainer} solid`,
        }}
      >
        <Typography variant='headingSmall'>
          {t('updateAvatar.title')}
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {!userContext.avatar && (
          <DroppableFileInput
            onFileChosen={(file: File) => setAvatar(file)}
            acceptedTypes={[
              FileTypes.JPEG,
              FileTypes.JPG,
              FileTypes.PNG,
              FileTypes.HEIC,
            ]}
            fileMaxSize={3}
          />
        )}
        {userContext.avatar && (
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            flexDirection='column'
          >
            <InlineImage
              fileDocumentId={userContext.avatar}
              style={{
                height: '100px',
                width: '100px',
                borderRadius: sys.borderRadius.round,
                marginBottom: '8px',
                objectFit: 'cover',
              }}
            />
            <Button
              onClick={() => deleteFile()}
              label={t('shared:delete')}
              variant='text'
              color='destructive'
            />
          </Box>
        )}
      </DialogContent>
      <DialogFooter>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            onClick={() => doUpload()}
            label={t('shared:save')}
            disabled={!newAvatar || loading}
          />
        </Box>
      </DialogFooter>
    </Dialog>
  );
};
